import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import Tab, { TabGroup } from "@amzn/meridian/tab";
import Link from "@amzn/meridian/link";
import {
  activityTemplate,
  defaultTime,
  opsClockActivities,
  groceryOpsClock,
  defaultOpsClockHeaders,
} from "./SsdSettingsConstant";
import Popover, { PopoverHeader } from "@amzn/meridian/popover";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import TimePicker from "@amzn/meridian/time-picker";
import undoTokens from "@amzn/meridian-tokens/base/icon/undo";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import Box from "@amzn/meridian/box";
import {
  isValidTimeFormat,
  trimAndCapitalizeFirstLetter,
} from "../../SettingsUtil";
import { SsdSettingsContext } from "./SsdSettingsContext";
import { logOperationalMetric } from "../../../overview/utils/utils";
import { metricProcessName } from "../../../util/Constants";
import { updateAdminSettings } from "./SsdSettingsNetworkAPI";
import UpdateResponseModal from "./UpdateResponseModal";
import {
  BUSINESS_VIEW_NAME,
  CORE_BUSINESS_TYPE_LABEL,
  MODAL_ERROR,
  MODAL_SUCCESS,
} from "../SettingsConstant";
import { getUserLogin } from "../../../resources/EmployeeResource";

const timePattern = /^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;

const getActivityByLabel = (data, label, businessType) => {
  return data.find((item) => item.label === label) || null;
};

const checkIfActivityIsEligibleForBusinessType = (activity, businessType) => {
  // Backwards compatibility which ensures even if businessType is not present we default to Core
  if (activity && activity.businessType !== undefined) {
    return activity.businessType === businessType;
  }
  return businessType === BUSINESS_VIEW_NAME.CORE;
};

const getRevertStateKey = (activityName, windowName) => {
  return `${activityName}#${windowName}`;
};

const getActivityTimeByActivityAndWindow = (
  opsClock,
  activityName,
  windowName,
) => {
  const opData = opsClock.find((item) => item.label === activityName);
  if (opData) {
    const activity = opData.window_list.find((win) => win.name === windowName);
    return activity && activity.time ? activity.time : defaultTime;
  }
  return defaultTime;
};

const createNewActivity = (template, activityName, windowName, time) => {
  const updatedTemplate = { ...template };

  updatedTemplate.label = activityName;
  updatedTemplate.name = activityName
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();
  updatedTemplate.window_list = updatedTemplate.window_list.map((window) => {
    if (window.name === windowName) {
      return { ...window, time };
    }
    return window;
  });

  return updatedTemplate;
};

const updateDefaultsToNull = (opsClock) => {
  return opsClock.map((obj) => {
    const { window_list } = obj;

    if (window_list) {
      const updatedWindowList = window_list.map((activity) => {
        if (activity.time === defaultTime) {
          return { ...activity, time: null };
        }
        return activity;
      });
      return { ...obj, window_list: updatedWindowList };
    }
    return obj;
  });
};

const removeAllNullTimeWindowListActivities = (opsClock) => {
  const hasNonNullTime = (activity) => {
    return activity.window_list.some((window) => window.time !== null);
  };
  return opsClock.filter(hasNonNullTime);
};

const getToBeSavedOpsClock = (opsClock) => {
  let toSaveOpsClock = updateDefaultsToNull(opsClock);
  toSaveOpsClock = removeAllNullTimeWindowListActivities(toSaveOpsClock);
  return toSaveOpsClock;
};

const getBusinessTypes = (opsClock) => {
  const opsClockWithBusinessType = opsClock.map((window) => ({
    ...window,
    businessType: window.businessType || BUSINESS_VIEW_NAME.CORE,
  }));

  return Array.from(
    new Set(
      opsClockWithBusinessType.map((window) => window.businessType).sort(),
    ),
  );
};

const getOpsClockHeaders = (opsClock) => {
  let headers = ["Activity"];
  if (!opsClock || opsClock.length === 0) {
    return defaultOpsClockHeaders;
  }
  for (const window of opsClock[0].window_list) {
    headers.push(window.name);
  }
  return headers;
};

const OpsClock = (props) => {
  const { viewModel, dispatch } = useContext(SsdSettingsContext);
  const opsClock = viewModel.settingsData.adminSettings["ops_clock"];
  const isGroceryEnabled =
    viewModel.settingsData.adminSettings["grocery_enabled"];
  const opsClockHeaders = getOpsClockHeaders(opsClock);
  const opsClockActivities = getMergedOpsClockActivities(isGroceryEnabled);
  const [currentOpsClock, setCurrentOpsClock] = useState(
    opsClock ? opsClock : [],
  );
  const [updatedOpsClock, setUpdatedOpsClock] = useState(currentOpsClock);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [revertStates, setRevertStates] = useState({});
  const [selectedBusinessType, setSelectedBusiness] = useState(
    BUSINESS_VIEW_NAME.CORE,
  );

  const handleBusinessChange = (newBusiness) => {
    setSelectedBusiness(newBusiness);
  };

  useEffect(() => {
    dispatch({ type: "FETCH_ADMIN_SETTINGS", data: { dispatcher: dispatch } });
    setCurrentOpsClock(opsClock ? opsClock : []);
    setShouldRefresh(false);
  }, [shouldRefresh]);

  const memoizedGetActivityByLabel = useMemo(() => getActivityByLabel, []);
  const memoizedGetActivityTimeByActivityAndWindow = useMemo(
    () => getActivityTimeByActivityAndWindow,
    [],
  );
  const memoizedGetBusinessTypes = useMemo(() => getBusinessTypes, []);

  const updateOpsClock = (activity, activityName, windowName, value) => {
    const labelExists = updatedOpsClock.some(
      (activity) => activity.label === activityName,
    );
    if (labelExists) {
      const newUpdatedOpsClock = updatedOpsClock.map((act) => {
        if (act.label === activity.label) {
          const updatedWindowList = act.window_list.map((window) => {
            if (window.name === windowName) {
              return { ...window, time: value };
            }
            return window;
          });
          return { ...act, window_list: updatedWindowList };
        }
        return act;
      });
      setUpdatedOpsClock(newUpdatedOpsClock);
    } else {
      const newActivity = createNewActivity(
        activityTemplate,
        activityName,
        windowName,
        value,
      );
      const newUpdatedOpsClock = [...updatedOpsClock, newActivity];
      setUpdatedOpsClock(newUpdatedOpsClock);
    }
  };

  const onCancel = () => {
    setUpdatedOpsClock(currentOpsClock);
    setRevertStates({});
  };
  const onSave = async () => {
    const toSaveOpsClock = getToBeSavedOpsClock(updatedOpsClock);
    const toSaveAdminSettings = {
      ...viewModel.settingsData.adminSettings,
      ops_clock: toSaveOpsClock,
      lastUpdatedBy: await getUserLogin(),
      lastUpdatedAt: Math.round(Date.now() / 1000),
    };
    const startTime = Date.now();
    updateAdminSettings(toSaveAdminSettings)
      .then((res) => {
        if (res.status === 200 && res.statusText === "OK") {
          dispatch({
            type: "UPDATE_ADMIN_SETTINGS",
            data: {
              adminSettings: toSaveAdminSettings,
              showUpdatePlanResponseModal: MODAL_SUCCESS,
              messageToDisplay: "Ops clock updated successfully",
            },
          });
          setUpdatedOpsClock(toSaveOpsClock);
          setShouldRefresh(true);
          setRevertStates({});
          logOperationalMetric(
            startTime,
            metricProcessName,
            "updateSsdAdminSettings",
            true,
          );
        } else {
          dispatch({
            type: "UPDATE_ADMIN_SETTINGS",
            data: {
              adminSettings: viewModel.settingsData.adminSettings,
              showUpdatePlanResponseModal: MODAL_ERROR,
              messageToDisplay: "Ops clock updated failed. Network error",
            },
          });
          setUpdatedOpsClock(currentOpsClock);
          setShouldRefresh(false);
          setRevertStates({});
          logOperationalMetric(
            startTime,
            metricProcessName,
            "updateSsdAdminSettings",
            false,
          );
        }
      })
      .catch((err) => {
        dispatch({
          type: "UPDATE_ADMIN_SETTINGS",
          data: {
            adminSettings: viewModel.settingsData.adminSettings,
            showUpdatePlanResponseModal: MODAL_ERROR,
            messageToDisplay: "Ops clock updated failed",
          },
        });
        setUpdatedOpsClock(currentOpsClock);
        setShouldRefresh(false);
        setRevertStates({});
        logOperationalMetric(
          startTime,
          metricProcessName,
          "updateSsdAdminSettings",
          false,
        );
      });
  };

  const renderTableCell = (
    tempData,
    activity,
    activityName,
    windowName,
    isEligible,
  ) => {
    const linkRef = useRef();
    const [open, setOpen] = useState(false);
    const time = activity
      ? memoizedGetActivityTimeByActivityAndWindow(
          currentOpsClock,
          activityName,
          windowName,
        )
      : defaultTime;

    const onRevert = () => {
      const originalValue = getActivityTimeByActivityAndWindow(
        currentOpsClock,
        activityName,
        windowName,
      );
      setRevertStates((prevState) => ({
        ...prevState,
        [getRevertStateKey(activityName, windowName)]: false,
      }));
      updateOpsClock(activity, activityName, windowName, originalValue);
    };

    const onTimeChange = (time) => {
      if (isValidTimeFormat(time, timePattern)) {
        setRevertStates((prevState) => ({
          ...prevState,
          [getRevertStateKey(activityName, windowName)]: true,
        }));
        updateOpsClock(activity, activityName, windowName, time);
      }
    };
    return (
      isEligible && (
        <TableCell key={windowName}>
          <React.Fragment>
            {revertStates[getRevertStateKey(activityName, windowName)] && (
              <Button type={"link"} onClick={onRevert}>
                <Icon tokens={undoTokens} />
              </Button>
            )}

            <Link
              type={"secondary"}
              onClick={() => setOpen(true)}
              ref={linkRef}
            >
              {getActivityTimeByActivityAndWindow(
                updatedOpsClock,
                activityName,
                windowName,
              )}
            </Link>

            <Popover
              anchorNode={linkRef.current}
              open={open}
              onClose={() => setOpen(false)}
              position="top"
            >
              <PopoverHeader closeLabel="Close">
                Provide an override
              </PopoverHeader>
              <Column>
                <Row width="100%" alignmentHorizontal="justify">
                  <Text type={"b300"}>Original Time </Text>
                  <Text type={"b300"}> {time} </Text>
                </Row>
                <Row width="100%" alignmentHorizontal="justify">
                  <Text type={"b300"}>New Time </Text>
                </Row>
                <Row width="100%" alignmentHorizontal="justify">
                  <TimePicker
                    placeholder="Pick a time"
                    value={getActivityTimeByActivityAndWindow(
                      updatedOpsClock,
                      activityName,
                      windowName,
                    )}
                    onChange={(time) => onTimeChange(time)}
                    width={300}
                    size="small"
                  />
                </Row>
              </Column>
            </Popover>
          </React.Fragment>
        </TableCell>
      )
    );
  };

  return (
    <div>
      <Row alignmentHorizontal="right">
        <TabGroup value={selectedBusinessType} onChange={handleBusinessChange}>
          {memoizedGetBusinessTypes(updatedOpsClock).map((business) => (
            <Tab value={business} key={`${business}`}>
              {business === BUSINESS_VIEW_NAME.CORE
                ? CORE_BUSINESS_TYPE_LABEL
                : trimAndCapitalizeFirstLetter(business)}
            </Tab>
          ))}
        </TabGroup>
      </Row>
      <Box type="fill" spacingInset="400">
        <Row alignmentHorizontal={"right"}>
          <Button onClick={() => onCancel()} type="primary" size={"small"}>
            Cancel
          </Button>
          <Button onClick={() => onSave()} type="primary" size={"small"}>
            Save
          </Button>
        </Row>
      </Box>
      <Table
        headerRows={1}
        showDividers={true}
        spacing="medium"
        showStripes={false}
      >
        <TableRow highlightOnHover={true}>
          {opsClockHeaders.map((header) => (
            <TableCell key={header}>{header}</TableCell>
          ))}
        </TableRow>
        {Object.keys(opsClockActivities).map((name) => {
          const activity = memoizedGetActivityByLabel(
            updatedOpsClock,
            opsClockActivities[name],
            selectedBusinessType,
          );
          const isEligible = checkIfActivityIsEligibleForBusinessType(
            activity,
            selectedBusinessType,
          );
          return (
            <TableRow key={name}>
              {isEligible && <TableCell>{name}</TableCell>}
              {opsClockHeaders
                .slice(1)
                .map((windowName) =>
                  renderTableCell(
                    updatedOpsClock,
                    activity,
                    opsClockActivities[name],
                    windowName,
                    isEligible,
                  ),
                )}
            </TableRow>
          );
        })}
      </Table>
      <UpdateResponseModal />
    </div>
  );
};

export default OpsClock;

export const getMergedOpsClockActivities = (isGroceryEnabled) => {
  return isGroceryEnabled
    ? { ...opsClockActivities, ...groceryOpsClock }
    : opsClockActivities;
};



// WEBPACK FOOTER //
// ./src/components/settings/settingsv2/ssd/OpsClock.js